import { Controller } from "@hotwired/stimulus"
import { TempFormHelper } from "~/helpers/temp_form_helper"
import { CurrencyHelper } from "~/helpers/currency_helper";
import { CostCalculator } from "~/helpers/costCalculator"

// This controller is used to verify the selected or currently filtered leads.
export default class extends Controller {
  static get values() {
    return {
      filters: { type: Object, default: {} },
      numberOfLeads: { type: Number, default: 0 },
      selectedLeadIds: { type: Array, default: [] },
      verifyCallsLeft: { type: Number, default: 0 },
      verifyOverageRequestsEnabled: { type: Boolean, default: false },
      singleCreditsBucket: { type: Boolean, default: false },
    }
  }

  static get targets() {
    return ["modalTitle", "modalText", "modalButton", "memberLearnMore"]
  }

  // This function is triggered using stimulus actions to submit a temporary form to verify the selected or filtered
  // leads.
  async submit() {
    const cost = CostCalculator.calculate("verify", this._numberOfLeadsToVerify(), this.singleCreditsBucketValue)

    // Check if insufficient credits and overage not enabled
    if (cost > this.verifyCallsLeftValue && !this.verifyOverageRequestsEnabledValue) {
      // Submit to backend to trigger checkout modal via turbo stream
      await this._submitForCheckout()
      return
    }

    // Existing flow: create form and show confirmation modal if needed
    let formToSubmit = TempFormHelper.create("/lead/verify", "post", (form) => {
      if (this.selectedLeadIdsValue.length > 0) {
        this.selectedLeadIdsValue.forEach(leadId => { TempFormHelper.addHiddenInput(form, "lead_ids[]", leadId) })
      } else {
        Object.entries(this.filtersValue).forEach(([key, value]) => { TempFormHelper.addHiddenInput(form, key, value) })
      }
    })

    formToSubmit.dataset.confirmDialogId = "confirm-verify-leads"

    const visualCost = CostCalculator.calculateVisualRepresentation("verify", this._numberOfLeadsToVerify(), this.singleCreditsBucketValue)
    const singleVerificationVisualCost = CostCalculator.calculateVisualRepresentation("verify", 1, this.singleCreditsBucketValue)
    const descriptionKey = this.singleCreditsBucketValue ? "description_unified" : "description"
    if (cost > this.verifyCallsLeftValue && this.verifyOverageRequestsEnabledValue) {
      formToSubmit.dataset.turboConfirm = "true"

      this.modalTitleTarget.innerText = i18n.t("js.back.lead_actions.verify_leads.overage.title")
      this.modalTextTarget.innerText = i18n.t(`js.back.lead_actions.verify_leads.overage.${descriptionKey}`, {
        count: this._numberOfLeadsToVerify(),
        credits: CurrencyHelper.numberWithDelimiter(visualCost)
      })
      this.modalButtonTarget.innerText = i18n.t("js.back.lead_actions.verify_leads.overage.confirm_button")
    } else if (visualCost > singleVerificationVisualCost) {
      formToSubmit.dataset.turboConfirm = "true"

      this.modalTitleTarget.innerText = i18n.t("js.back.lead_actions.verify_leads.verify.title")
      this.modalTextTarget.innerText = i18n.t(`js.back.lead_actions.verify_leads.verify.${descriptionKey}`, {
        count: this._numberOfLeadsToVerify(),
        credits: CurrencyHelper.numberWithDelimiter(visualCost)
      })
      this.modalButtonTarget.innerText = i18n.t("js.back.lead_actions.verify_leads.verify.confirm_button")
    }

    formToSubmit.addEventListener("turbo:submit-end", () => {
      this.verifyCallsLeftValue -= this._numberOfLeadsToVerify()
    })
    formToSubmit.requestSubmit()
  }

  async _submitForCheckout() {
    const formData = new URLSearchParams()

    if (this.selectedLeadIdsValue.length > 0) {
      this.selectedLeadIdsValue.forEach(leadId => {
        formData.append("lead_ids[]", leadId)
      })
    } else {
      Object.entries(this.filtersValue).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch("/lead/verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body: formData.toString(),
      })

      // If turbo stream response (insufficient credits), render it
      if (response.headers.get("Content-Type")?.includes("turbo-stream")) {
        const { Turbo } = await import("@hotwired/turbo-rails")
        Turbo.renderStreamMessage(await response.text())
      }
    } catch (error) {
      console.error("Error submitting for checkout:", error)
    }
  }

  _numberOfLeadsToVerify() {
    if (this.selectedLeadIdsValue.length > 0) {
      return this.selectedLeadIdsValue.length
    } else {
      return this.numberOfLeadsValue
    }
  }

  // This function is triggered using stimulus actions to update the selected leads value.
  updateSelection(event) {
    if (event.detail.allSelected) {
      this.selectedLeadIdsValue = []
    } else if (event.detail.dataSets) {
      this.selectedLeadIdsValue = event.detail.dataSets.map(dataSet => dataSet.leadId || dataSet.recordId).filter(Boolean)
    } else {
      this.selectedLeadIdsValue = []
    }
  }
}
